import type { BoxProps, SystemStyleObject } from "@chakra-ui/react"
import { Box, Grid, Slider, Spinner, Text } from "@chakra-ui/react"

export type SliderSwitchProps = {
  /** Current step index (0 to stepCount - 1). */
  value: number
  /** Number of discrete steps. First and last are inset by half thumb width from track edges. */
  stepCount: number
  onChange: (stepIndex: number) => void
  onChangeEnd?: (stepIndex: number) => void
  /** Optional labels shown under the track (one per step). */
  labels?: string[]
  disabled?: boolean
  /** Thumb width in px; used with measured track width to compute first/last step positions. Default 24. */
  thumbWidthPx?: number
  /** Width of the slider container (measured for step positioning). Default "full". */
  width?: BoxProps["width"]
  /** Props passed to the root Box. */
  rootProps?: BoxProps
  /** Slider track/thumb styling overrides. */
  sliderSx?: SystemStyleObject
} & Omit<BoxProps, "width" | "onChange">

/**
 * A discrete slider whose first and last steps are positioned so the thumb
 * doesn't overshoot the track edges. Step positions are derived from the
 * measured width of the track DOM element and the thumb width.
 */
export default function SliderSwitch({
  value,
  stepCount,
  onChange,
  onChangeEnd,
  labels,
  disabled = false,
  thumbWidthPx = 24,
  width = "full",
  rootProps,
  sliderSx,
  ...boxProps
}: SliderSwitchProps) {
  const handleChange = (v: number) => onChange(v)
  const handleChangeEnd = (v: number) => (onChangeEnd ?? onChange)(v)

  return (
    <Box width={width} {...rootProps} {...boxProps}>
      <Box
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Slider.Root
          variant="outline"
          aria-label={[labels?.join(", ") ?? "Slider"]}
          value={[value]}
          step={1}
          min={0}
          max={stepCount - 1}
          disabled={disabled}
          onValueChange={(e: { value: number[] }) =>
            handleChange(e.value[0] ?? 0)
          }
          onValueChangeEnd={(e: { value: number[] }) =>
            handleChangeEnd(e.value[0] ?? 0)
          }
          colorPalette="gray"
          size="sm"
          css={{
            "& .chakra-slider__track": {
              h: 4
            },
            "& .chakra-slider__thumb": {
              w: 6,
              h: "100%"
            },
            ...sliderSx
          }}
        >
          <Slider.Control>
            <Slider.Track bg="whiteAlpha.300">
              <Slider.Range />
            </Slider.Track>
            <Slider.Thumb index={0}>
              <Slider.HiddenInput />
              {/* NOTE: probably need to use an explicit loading state */}
              {disabled && <Spinner size="xs" />}
            </Slider.Thumb>
          </Slider.Control>
        </Slider.Root>
      </Box>
      {labels && labels.length > 0 && (
        <Grid
          templateColumns={`repeat(${labels.length}, 1fr)`}
          mt={0.5}
          px={0.5}
          fontSize="xs"
          gap={0}
        >
          {labels.map((label, i) => (
            <Text
              key={i}
              as="span"
              color="whiteAlpha.800"
              textAlign={
                i === 0 ? "left" : i === labels.length - 1 ? "right" : "center"
              }
              textTransform="uppercase"
            >
              {label}
            </Text>
          ))}
        </Grid>
      )}
    </Box>
  )
}
